import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import type { ActivityResult } from '@/services/activities';

// ============================================
// TYPES
// ============================================

interface ActivityResultModalProps {
  isOpen: boolean;
  result: ActivityResult | null;
  activityType: string;
  onClose: () => void;
}

// ============================================
// CONSTANTS
// ============================================

const ACTIVITY_NAMES: Record<string, string> = {
  meditation: 'Тишина',
  breathing: 'Ветер',
  reflection: 'Рефлексия',
};

const ACTIVITY_EMOJIS: Record<string, string> = {
  meditation: '🧘',
  breathing: '🌬️',
  reflection: '✍️',
};

const CHAR_NAMES: Record<string, { name: string; emoji: string }> = {
  charAttention: { name: 'Внимание', emoji: '🎯' },
  charEmotionalRegulation: { name: 'Эмоц. регуляция', emoji: '🌊' },
  charBodyEnergy: { name: 'Энергия тела', emoji: '💪' },
  charMeaning: { name: 'Смысл', emoji: '🧭' },
  charCommunication: { name: 'Коммуникация', emoji: '💬' },
  charTrustBonds: { name: 'Связи', emoji: '🤝' },
  charAgency: { name: 'Субъектность', emoji: '🚀' },
  charCreativity: { name: 'Творчество', emoji: '🎨' },
};

// ============================================
// FATIGUE BAR COMPONENT
// ============================================

function FatigueChangeBar({
  before,
  after,
}: {
  before: number;
  after: number;
}) {
  const reduction = before - after;

  return (
    <div className="bg-primary-800/50 rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-primary-300 text-sm">Усталость</span>
        <div className="flex items-center gap-2">
          <span className="text-primary-500">{before}%</span>
          <span className="text-accent-teal">→</span>
          <span className="text-accent-teal font-bold">{after}%</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative h-3 bg-primary-700 rounded-full overflow-hidden">
        {/* Before state (faded) */}
        <motion.div
          initial={{ width: `${before}%` }}
          animate={{ width: `${after}%` }}
          transition={{ duration: 1, ease: 'easeOut', delay: 0.3 }}
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-accent-teal to-accent-purple rounded-full"
        />
      </div>

      {/* Reduction badge */}
      <motion.div
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="mt-2 flex justify-end"
      >
        <span className="text-accent-teal text-sm font-medium">
          ⚡ -{reduction}
        </span>
      </motion.div>
    </div>
  );
}

// ============================================
// XP GAINS COMPONENT
// ============================================

function XpGainsSection({ gains }: { gains: ActivityResult['xpGains'] }) {
  if (!gains || gains.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="mb-4"
    >
      <p className="text-primary-400 text-sm mb-2">Прокачаны характеристики:</p>
      <div className="space-y-2">
        {gains.map((gain, i) => {
          const info = CHAR_NAMES[gain.char] || { name: gain.char, emoji: '📊' };
          return (
            <motion.div
              key={gain.char}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + i * 0.1 }}
              className="flex items-center justify-between bg-primary-800/30 rounded-lg px-3 py-2"
            >
              <div className="flex items-center gap-2">
                <span>{info.emoji}</span>
                <span className="text-primary-200 text-sm">{info.name}</span>
              </div>
              <span className="text-accent-teal text-sm font-medium">+{gain.amount}</span>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}

// ============================================
// CHRONICLE PREVIEW
// ============================================

function ChroniclePreview({ narrative }: { narrative: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.9 }}
      className="bg-primary-800/30 rounded-xl p-4 mb-4 border-l-2 border-accent-moon/50"
    >
      <p className="text-primary-400 text-xs mb-2 font-medium">📜 Хроника</p>
      <p className="text-primary-200 text-sm italic leading-relaxed line-clamp-4">
        "{narrative}"
      </p>
    </motion.div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function ActivityResultModal({
  isOpen,
  result,
  activityType,
  onClose,
}: ActivityResultModalProps) {
  if (!result) return null;

  const activityName = ACTIVITY_NAMES[activityType] || 'Практика';
  const activityEmoji = ACTIVITY_EMOJIS[activityType] || '✨';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-b from-primary-900 to-primary-950 rounded-2xl p-6 w-full max-w-sm border border-primary-700/50 max-h-[85vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="text-center mb-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', delay: 0.1 }}
                className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-accent-teal/30 to-accent-purple/30 flex items-center justify-center mb-4 border border-accent-teal/30"
              >
                <span className="text-4xl">{activityEmoji}</span>
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-xl font-serif text-accent-moon"
              >
                {activityName} завершена
              </motion.h2>
            </div>

            {/* Fatigue change */}
            <FatigueChangeBar
              before={result.fatigueBefore}
              after={result.fatigueAfter}
            />

            {/* XP Gains */}
            <XpGainsSection gains={result.xpGains} />

            {/* Chronicle entry preview */}
            {result.chronicleEntry?.narrative && (
              <ChroniclePreview narrative={result.chronicleEntry.narrative} />
            )}

            {/* Close button */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
            >
              <Button onClick={onClose} fullWidth>
                ← В город
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
